import { Trans, useLingui } from '@lingui/react/macro'
import { type ReactElement, useState } from 'react'

import { loadNudgeDismissed, persistNudgeDismissed } from './install-nudge-dismissal.js'
import { isRunningStandalone } from './is-standalone.js'
import { useInstallPrompt } from './use-install-prompt.js'

/**
 * A persistent, dismissible install banner for the post-discovery surfaces (login / authenticated).
 * The {@link InstallShell} only appears while boot-time discovery runs or when it fails, so a
 * visitor whose discovery succeeds is taken straight to login and would otherwise never be prompted
 * to install. Installing is the whole point of the PWA: the service worker precaches the shell so
 * the app keeps booting once the delivery origin is DNS-blocked. The nudge therefore rides along on
 * the login screen and only steps aside when the app is already installed (running standalone) or
 * the visitor dismisses it.
 */
export const InstallNudge = (): null | ReactElement => {
  const { promptInstall, status } = useInstallPrompt()
  // The nudge renders only on the login surface, inside the lingui provider, so it localises
  // directly through the runtime `t`/`<Trans>` macros (unlike the provider-less {@link InstallShell}).
  const { t } = useLingui()
  // Snapshot the installed-vs-tab check once at mount — it cannot change without a fresh launch.
  const [standalone] = useState(isRunningStandalone)
  // Seed dismissal from storage (PT-5644) so a prior dismissal survives relaunches and any remount
  // of the login surface this nudge lives on, instead of resetting to false on every mount.
  const [dismissed, setDismissed] = useState(loadNudgeDismissed)

  const dismiss = (): void => {
    setDismissed(true)
    persistNudgeDismissed()
  }

  // Already installed, or previously dismissed — nothing to nudge.
  if (standalone || dismissed || status === 'installed') return null

  return (
    <aside aria-label={t`Install app`} className="install-nudge">
      <div className="install-nudge__body">
        <p className="install-nudge__text">
          <Trans>Add the app to your home screen for uninterrupted access.</Trans>
        </p>

        {status === 'available' ? (
          <button className="install-nudge__cta" type="button" onClick={() => void promptInstall()}>
            <Trans>Install app</Trans>
          </button>
        ) : (
          // iOS Safari (and any browser that never offered a deferred prompt) — manual guide.
          <p className="install-nudge__hint">
            <Trans>
              On iPhone, tap the Share button and choose <strong>Add to Home Screen</strong>.
            </Trans>
          </p>
        )}
      </div>

      <button aria-label={t`Dismiss`} className="install-nudge__dismiss" type="button" onClick={dismiss}>
        &times;
      </button>
    </aside>
  )
}
